import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Tabs,
  Switch,
  Alert,
  Divider,
  Checkbox,
  DatePicker
} from 'antd';
import moment from 'moment';
import normalizeDate from 'util/normalizers/normalizeDate';
import InboundInvoiceSyncStatus from 'pages/app/invoices/InboundInvoiceSyncStatus';
import ResCol from 'components/forms/ResCol';
import {
  DrawerEdit,
  FormItemWrap,
  ResourceEdit,
  useAuthContext,
  useCrudEditContext,
  api,
  HttpErrorResponseModel,
  normalizeToSelect,
  normalizeFromSelect,
  RemoteSelectCrud,
  configManager
} from 'react-structure-admin';

import {
  closeSettings,
  getServiceApproximateTaxes,
  updateSettings
} from 'stores/companies/companyActions';
import { useDispatch } from 'react-redux';
import InputPercent from 'components/InputPercent';
import {
  AddressForForeign,
  AddressV2,
  CfopSelect,
  SearchInput,
  InputNumeric,
  TooltipInfoIcon
} from 'components';
import ProductUnitOfMeasurement from 'pages/app/products/ProductUnitOfMeasurement';
import {
  APPROXIMATETAXES_DETAILED,
  APPROXIMATETAXES_DISABLED,
  APPROXIMATETAXES_SIMPLIFIED,
  display,
  issCalculationModesDisplay,
  PRODUCTINVOICE,
  SERVICEINVOICE
} from 'constants/types';
import ObjectHelper from 'util/helpers/ObjectHelper';
import {
  cityNormalizeFromSelect,
  cityNormalizeToSelect
} from 'util/normalizers';
import TaxationGroupEdit from 'pages/app/taxationGroups/TaxationGroupEdit';
import { emailValidator } from 'util/validators';
import EnvironmentTypeSelect from 'components/EnvironmentTypeSelect';
import useSubscription from 'core/useSubscription';
import { INBOUND_INVOICES } from 'constants/features';

const { TabPane } = Tabs;

const INBOUND_MODELS = [
  {
    settingsKey: 'productInvoice',
    resource: 'inbound-product-invoices',
    label: 'NF-e',
    retentionNote: 'A SEFAZ mantém cerca de 90 dias de histórico.'
  },
  {
    settingsKey: 'serviceInvoice',
    resource: 'inbound-service-invoices',
    label: 'NFS-e',
    retentionNote: ''
  }
];

const issueTypeDescriptions = {
  normal: 'Web Service Município',
  website: 'Site/Portal',
  annfs: 'API Ambiente Nacional',
  alt: 'Web Service Município (Alternativo)'
};

// Espelha CompanySettingsInbound.MaxRetroactiveDays no backend, que recusa data mais antiga.
const inboundMaxRetroactiveDays = 90;

const isOutsideInboundWindow = (date) =>
  date &&
  (date.isBefore(moment().subtract(inboundMaxRetroactiveDays, 'days'), 'day') ||
    date.isAfter(moment(), 'day'));

const CompanySettings = ({ data, ...rest }) => {
  const dispatch = useDispatch();
  const { hasFeature } = useSubscription();
  const hasInbound = hasFeature(INBOUND_INVOICES);
  const { company, isAdmin, isAuthenticated, refresh } = useAuthContext();
  const { form } = useCrudEditContext();
  const { affiliate, settings } = data;
  const [selectedOption, setSelectedOption] = useState(
    data.settings.addressResolutionMode
  );
  const [affiliateEnabled, setAffiliateEnabled] = useState(
    affiliate.enabledAffiliateInvoices
  );

  const [environmentType, setEnvironmentType] = useState(
    settings.serviceInvoice.environmentType
  );

  const [autoIssueInvoice, setAutoIssueInvoice] = useState(
    affiliate.autoIssueInvoice
  );

  const [issueType, setIssueType] = useState(settings.serviceInvoice.issueType);

  const [providerSettings, setProviderSettings] = useState(
    data.providerSettings ?? {}
  );

  const [issueTypes, setIssueTypes] = useState([]);

  const [approximateTaxes, setApproximateTaxes] = useState(
    settings.serviceInvoice.approximateTaxes
  );

  const [tab, setTab] = useState('producer');

  const [checkedCalculationMode, setCheckedCalculationMode] = useState(
    settings?.serviceInvoice.issCalculationMode ?? []
  );

  const [paginationMode, setpaginationMode] = useState(settings.paginationMode);

  const isSimplesNacional =
    company.taxRegime != null && company.taxRegime !== 'regimeNormal';

  const allowsSimplesNacionalRate =
    isSimplesNacional &&
    ((company.nationalServiceInvoiceRegimes !== null &&
      company.nationalServiceInvoiceRegimes !== 'none') ||
      providerSettings.useNationalLayout);

  useEffect(() => {
    form.validateFields(['nextNumber', 'series', 'nextBatchNumber']);
  }, [affiliateEnabled]);

  useEffect(() => {
    api
      .fetch('companies/:companyId/issue-types', {
        companyId: company.id,
        environmentType
      })
      .then(({ data }) => {
        setIssueTypes(data.result);
        if (!data.result.includes(issueType)) {
          setIssueType(null);
          form.setFieldsValue({
            settings: { serviceInvoice: { issueType: null } }
          });
        }
      });
  }, [environmentType]);

  useEffect(() => {
    if (issueType) {
      api
        .fetch('finance-offices/settings/', {
          issueType
        })
        .then(({ data }) => {
          setProviderSettings({
            ...data.result.serviceInvoice.providerSettings
          });
        });
    }
  }, [issueType]);

  const onAfterUpdate = ({ data }, extras) => {
    const { result } = data;

    if (extras.taxReformFieldsModified) {
      if (result && isAuthenticated()) {
        refresh();
      }
    }

    message.success('Registro salvo com sucesso.');

    const lowered = extras.loweredInbound || [];

    if (lowered.length > 0) {
      confirmInboundReimport(lowered);
    }
  };

  const confirmInboundReimport = (models) => {
    const retentionNotes = models
      .map((model) => model.retentionNote)
      .filter(Boolean)
      .join(' ');

    Modal.confirm({
      title: 'Reimportar histórico',
      content: `Você definiu uma data de início anterior para ${models
        .map((model) => `${model.label} (${moment(model.startDate).format('DD/MM/YYYY')})`)
        .join(' e ')}. Deseja reimportar as notas recebidas a partir dela? ${retentionNotes} A importação pode levar alguns minutos.`,
      okText: 'Reimportar',
      cancelText: 'Agora não',
      onOk: () =>
        Promise.all(
          models.map((model) =>
            api.post(`${model.resource}/reimport`).then((response) => ({
              model,
              error:
                response instanceof HttpErrorResponseModel
                  ? response.errors?.[0]?.message ||
                    'Não foi possível reimportar o histórico.'
                  : null
            }))
          )
        ).then((results) => {
          const failed = results.filter((result) => result.error);

          failed.forEach((result) =>
            message.error(`${result.model.label}: ${result.error}`)
          );

          if (failed.length < results.length) {
            message.success(
              'Reimportação solicitada. As notas anteriores vão aparecer conforme forem importadas.'
            );
          }
        })
    });
  };

  const normalizeProfileSettings = (
    { serviceInvoice, productInvoice },
    factory
  ) => {
    return {
      serviceInvoice: {
        ...serviceInvoice,
        taxationGroup:
          serviceInvoice != null ? factory(serviceInvoice.taxationGroup) : null
      },
      productInvoice: {
        ...productInvoice,
        taxationGroup:
          productInvoice != null ? factory(productInvoice.taxationGroup) : null
      }
    };
  };

  const normalizeCountryFromSelect = (country) => {
    if (!country || selectedOption !== 'useForeignAddress') {
      return { code: 1058, name: 'Brasil' };
    }

    return { code: country.value, name: country.label };
  };

  const normalizeCountryToSelect = (country) => {
    if (!country) {
      return null;
    }

    return { value: country.code, label: country.name };
  };

  const handleBeforeSubmit = (formData) => {
    const { settings = {} } = formData;

    if (
      !isAdmin() &&
      (settings?.productInvoice?.environmentType === 'production' ||
        settings?.serviceInvoice?.environmentType === 'production') &&
      configManager.getConfig().isTestMode
    ) {
      message.error('Ambiente de produção não está disponível no Sandbox.');
      return;
    }

    handleSubmit(formData);
  };

  const handleSubmit = (formData) => {
    const { settings, producer, affiliate } = data;
    const {
      producer: newProducer,
      affiliate: newAffiliate,
      settings: newSettings
    } = formData;
    const { defaultAddress = {}, serviceInvoice = {} } = settings;
    const {
      defaultAddress: newDefaultAddress = {},
      serviceInvoice: newServiceInvoice = {}
    } = newSettings;

    // Reduzir a data de início das notas recebidas só tem efeito com reimportação (o NSU só
    // avança). Detectamos aqui e, após salvar, oferecemos reimportar — na ordem certa: salva
    // primeiro (a reimportação usa a data já persistida como corte), depois pergunta.
    const loweredInbound = INBOUND_MODELS.map((model) => {
      const previous = settings[model.settingsKey]?.inbound || {};
      const current = newSettings[model.settingsKey]?.inbound || {};
      const startDate = current.startDate;
      const lowered =
        !!current.enabled &&
        !!previous.enabled &&
        !!previous.startDate &&
        !!startDate &&
        moment(startDate).isBefore(moment(previous.startDate), 'day');

      return lowered ? { ...model, startDate } : null;
    }).filter(Boolean);

    dispatch(
      updateSettings(
        {
          id: company.id,
          ...formData,
          producer: {
            ...producer,
            ...newProducer,
            ...normalizeProfileSettings(newProducer, normalizeFromSelect)
          },
          affiliate: {
            ...affiliate,
            ...newAffiliate,
            ...normalizeProfileSettings(newAffiliate, normalizeFromSelect)
          },
          settings: {
            ...settings,
            ...newSettings,
            paginationMode,
            serviceInvoice: {
              ...serviceInvoice,
              ...newServiceInvoice,
              simplesNacionalRate: !allowsSimplesNacionalRate
                ? null
                : newServiceInvoice.simplesNacionalRate ??
                serviceInvoice.simplesNacionalRate
            },
            defaultAddress:
              Object.keys(newDefaultAddress).length > 0
                ? {
                  ...(defaultAddress || {}),
                  ...newDefaultAddress,
                  city:
                    selectedOption === 'useForeignAddress'
                      ? null
                      : newDefaultAddress.city
                        ? cityNormalizeFromSelect(newDefaultAddress.city)
                        : defaultAddress.city,
                  country:
                    newDefaultAddress.country ||
                      selectedOption !== 'useForeignAddress'
                      ? normalizeCountryFromSelect(newDefaultAddress.country)
                      : defaultAddress.country
                }
                : defaultAddress
          }
        },
        (response) =>
          onAfterUpdate(response, {
            taxReformFieldsModified:
              settings.taxReformFieldsEnabled !==
              newSettings.taxReformFieldsEnabled,
            loweredInbound
          })
      )
    );
  };

  const handleCancel = () => {
    dispatch(closeSettings());
  };

  const handleAutoIssueInvoice = (value) => {
    setAutoIssueInvoice(value);
  };

  const handleChangeAffiliateEnabled = (value) => {
    setAffiliateEnabled(value);
  };

  const getApproximateTaxesIbpt = (taxationGroupId) => {
    dispatch(
      getServiceApproximateTaxes(taxationGroupId, ({ data: { result } }) => {
        const {
          nationalTax,
          municipalTax,
          stateTax,
          importTax,
          expiresAt,
          version
        } = result;

        form.setFieldsValue({
          settings: {
            serviceInvoice: {
              approximateTaxes: {
                national: nationalTax,
                municipal: municipalTax,
                state: stateTax,
                imported: importTax,
                expiresAt,
                version,
                simplesNacionalRate: null
              }
            }
          }
        });
      })
    );
  };

  const handleChangedApproximateTaxes = (
    changedApproximateTaxes,
    allValues
  ) => {
    const { federalServiceCode } = allValues?.producer?.serviceInvoice ?? {};
    setApproximateTaxes(allValues?.settings?.serviceInvoice?.approximateTaxes);

    if (changedApproximateTaxes?.useIbpt === false) {
      form.setFieldsValue({
        settings: {
          serviceInvoice: {
            approximateTaxes: {
              national: 0,
              municipal: 0,
              state: 0,
              imported: 0,
              expiresAt: null,
              version: null
            }
          }
        }
      });
    }

    if (federalServiceCode && changedApproximateTaxes?.useIbpt === true) {
      getApproximateTaxesIbpt(federalServiceCode);
    }
  };

  const handleChangedFederalServiceCode = (
    changedFederalServiceCode,
    allValues
  ) => {
    if (
      changedFederalServiceCode &&
      allValues?.settings?.serviceInvoice?.approximateTaxes?.useIbpt === true &&
      changedFederalServiceCode.length >= 4
    ) {
      getApproximateTaxesIbpt(changedFederalServiceCode);
    }
  };

  const handleChangedValues = (changedValues, allValues) => {
    const { settings, producer } = changedValues;

    if (settings?.serviceInvoice?.environmentType) {
      setEnvironmentType(settings.serviceInvoice.environmentType);
    }

    if (settings?.serviceInvoice?.issueType) {
      setIssueType(settings.serviceInvoice.issueType);
    }

    if (ObjectHelper.hasProperty(settings?.serviceInvoice?.approximateTaxes)) {
      handleChangedApproximateTaxes(
        settings.serviceInvoice.approximateTaxes,
        allValues
      );
    }

    if (producer?.serviceInvoice?.federalServiceCode !== undefined) {
      handleChangedFederalServiceCode(
        producer?.serviceInvoice?.federalServiceCode,
        allValues
      );
    }
  };

  const handleBeforeBinding = (data) => {
    const { settings } = data;
    const defaultAddress = settings.defaultAddress || {};

    return {
      ...data,
      producer: {
        ...data.producer,
        ...normalizeProfileSettings(data.producer, normalizeToSelect)
      },
      affiliate: {
        ...data.affiliate,
        ...normalizeProfileSettings(data.affiliate, normalizeToSelect)
      },
      settings: {
        ...settings,
        defaultAddress: {
          ...defaultAddress,
          city: cityNormalizeToSelect(defaultAddress.city),
          country: normalizeCountryToSelect(defaultAddress.country)
        }
      }
    };
  };

  const handleSelectChange = (value) => {
    setSelectedOption(value);
  };

  const supportedIssCalculationModes = [
    'suppressIssAmountOnSimplesNacional',
    'requiresIssBaseTax'
  ];
  const issCalculationModes = supportedIssCalculationModes.map((type) => ({
    label: issCalculationModesDisplay[type],
    value: type
  }));

  const handleChangeTabs = (key) => {
    setTab(key);
  };

  const handleChangeCalculationMode = (selectedValues) => {
    setCheckedCalculationMode(selectedValues);
  };

  const handleChangePaginationMode = (value) => {
    setpaginationMode(value);
  };

  const nextNumberLabel =
    issueType == 'annfs' ? 'Número da próxima DPS' : 'Número do próximo RPS';

  const seriesLabel = issueType == 'annfs' ? 'Série da DPS' : 'Série do RPS';

  return (
    <DrawerEdit
      {...rest}
      data={data}
      onSubmit={handleBeforeSubmit}
      onBeforeBinding={handleBeforeBinding}
      cancelEdit={handleCancel}
      onAfterUpdate={onAfterUpdate}
      onValuesChange={handleChangedValues}
      updateFormOnDataChanged={false}
      size="md"
      title="Configurações"
    >
      <Form layout="vertical">
        <Tabs>
          {!company.useCombinedInvoice && (
            <TabPane tab="NFS-e" key="nfse" forceRender>
              <Row>
                <ResCol span={data.settings.serviceInvoice.useRps ? 12 : 8}>
                  <FormItemWrap
                    name={['settings', 'serviceInvoice', 'environmentType']}
                    label="Ambiente"
                    tooltip={{
                      title: (
                        <div
                          style={{
                            maxWidth: 520,
                            lineHeight: 1.5
                          }}
                        >
                          <div style={{ marginBottom: 8 }}>
                            <strong>Produção:</strong>
                            <div>
                              Emissão com validade fiscal junto à Prefeitura ou
                              Ambiente Nacional.
                            </div>
                          </div>

                          <div style={{ marginBottom: 8 }}>
                            <strong>Homologação:</strong>
                            <div>
                              Testes no ambiente de homologação da Prefeitura ou
                              Ambiente Nacional, sem validade fiscal. Pode
                              exigir habilitação prévia na prefeitura.
                            </div>
                          </div>

                          <div>
                            <strong>
                              Simulação (Recomendado para testes):
                            </strong>
                            <div>
                              Valida o schema e simula a autorização localmente,
                              sem envio à prefeitura.
                            </div>
                          </div>
                        </div>
                      ),
                      overlayStyle: { maxWidth: 550 },
                      placement: 'right'
                    }}
                  >
                    <EnvironmentTypeSelect invoiceModel={SERVICEINVOICE} />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={data.settings.serviceInvoice.useRps ? 12 : 8}>
                  <FormItemWrap
                    name={['settings', 'serviceInvoice', 'issueType']}
                    label="Método de emissão"
                  >
                    <Select>
                      {issueTypes.map((c) => (
                        <Select.Option value={c}>
                          {issueTypeDescriptions[c]}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItemWrap>
                </ResCol>

                {providerSettings.provider !== 'softplan' &&
                  (providerSettings.useInvoiceNumber ||
                    providerSettings.useRps) ? (
                  <ResCol span={8}>
                    <FormItemWrap
                      name={['settings', 'serviceInvoice', 'nextNumber']}
                      label={
                        data.settings.serviceInvoice.useRps
                          ? nextNumberLabel
                          : 'Número da próxima NFS-e'
                      }
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </FormItemWrap>
                  </ResCol>
                ) : null}

                {providerSettings.provider === 'softplan' ? (
                  <ResCol span={8}>
                    <FormItemWrap
                      name={['settings', 'serviceInvoice', 'authNumber']}
                      label="AEDF-e"
                    >
                      <InputNumeric style={{ width: '100%' }} maxLength={11} />
                    </FormItemWrap>
                  </ResCol>
                ) : null}

                {data.settings.serviceInvoice.useBatchNumber && (
                  <ResCol span={8}>
                    <FormItemWrap
                      name={['settings', 'serviceInvoice', 'nextBatchNumber']}
                      label="Número do próximo Lote"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={99999999999}
                      />
                    </FormItemWrap>
                  </ResCol>
                )}
                {data.settings.serviceInvoice.useSeries && (
                  <ResCol span={8}>
                    <FormItemWrap
                      name={['settings', 'serviceInvoice', 'series']}
                      label={
                        data.settings.serviceInvoice.useRps
                          ? seriesLabel
                          : 'Série'
                      }
                    >
                      <Input maxLength={10} />
                    </FormItemWrap>
                  </ResCol>
                )}
                {providerSettings.authentication === 'userName' && (
                  <>
                    <ResCol span={8}>
                      <FormItemWrap
                        label={
                          providerSettings.provider == 'elmarTecnologia'
                            ? 'AppKey'
                            : 'Usuário'
                        }
                        name={['settings', 'serviceInvoice', 'userName']}
                        required
                      >
                        <Input maxLength={100} />
                      </FormItemWrap>
                    </ResCol>
                    <ResCol span={8}>
                      <FormItemWrap
                        label={
                          providerSettings.provider == 'elmarTecnologia'
                            ? 'Token'
                            : 'Senha'
                        }
                        name={['settings', 'serviceInvoice', 'password']}
                        required
                      >
                        <Input maxLength={500} type="password" />
                      </FormItemWrap>
                    </ResCol>
                  </>
                )}
                {providerSettings.authentication === 'token' && (
                  <>
                    <ResCol
                      span={
                        !data.settings.serviceInvoice.useSeries &&
                          !data.settings.serviceInvoice.useBatchNumber
                          ? 16
                          : 16
                      }
                    >
                      <FormItemWrap
                        label="Token/Senha"
                        name={['settings', 'serviceInvoice', 'password']}
                      >
                        <Input maxLength={500} type="password" />
                      </FormItemWrap>
                    </ResCol>
                  </>
                )}
                <FormItemWrap
                  name={['settings', 'serviceInvoice', 'useRps']}
                  hidden
                >
                  <Input />
                </FormItemWrap>
              </Row>

              {providerSettings.provider === 'sigep' ? (
                <ResCol span={12}>
                  <FormItemWrap
                    name={['settings', 'serviceInvoice', 'authNumber']}
                    label="Chave privada"
                    required
                  >
                    <Input maxLength={40} type="password" />
                  </FormItemWrap>
                </ResCol>
              ) : null}

              {hasInbound && (
                <>
                  <Divider orientation="left">Notas recebidas</Divider>
                  <Row gutter={16}>
                    <ResCol span={8}>
                      <FormItemWrap
                        name={['settings', 'serviceInvoice', 'inbound', 'enabled']}
                        label="Importar automaticamente"
                        tooltip="Busca automaticamente no Ambiente de Dados Nacional as NFS-e emitidas contra o CNPJ da empresa. Exige certificado digital válido cadastrado."
                        valuePropName="checked"
                      >
                        <Switch />
                      </FormItemWrap>
                    </ResCol>
                    <ResCol span={8}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                          prev?.settings?.serviceInvoice?.inbound?.enabled !==
                          cur?.settings?.serviceInvoice?.inbound?.enabled
                        }
                      >
                        {({ getFieldValue }) => {
                          const enabled = getFieldValue([
                            'settings',
                            'serviceInvoice',
                            'inbound',
                            'enabled'
                          ]);

                          return (
                            <FormItemWrap
                              name={['settings', 'serviceInvoice', 'inbound', 'startDate']}
                              label="Importar a partir de"
                              tooltip={`Só notas emitidas a partir desta data são importadas (as anteriores são descartadas). No máximo ${inboundMaxRetroactiveDays} dias retroativos. Reduzir a data e salvar oferece reimportar o histórico a partir dela.`}
                              rules={[
                                {
                                  required: enabled,
                                  message: 'Informe a partir de quando importar.'
                                }
                              ]}
                              getValueProps={(value) => ({
                                value: value ? normalizeDate(value) : null
                              })}
                            >
                              <DatePicker
                                format="DD/MM/YYYY"
                                style={{ width: '100%' }}
                                disabledDate={isOutsideInboundWindow}
                              />
                            </FormItemWrap>
                          );
                        }}
                      </Form.Item>
                    </ResCol>
                  </Row>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                      prev?.settings?.serviceInvoice?.inbound?.enabled !==
                      cur?.settings?.serviceInvoice?.inbound?.enabled
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue([
                        'settings',
                        'serviceInvoice',
                        'inbound',
                        'enabled'
                      ]) ? (
                        <Row>
                          <ResCol span={16}>
                            <InboundInvoiceSyncStatus
                              detailed
                              resource="inbound-service-invoices"
                            />
                          </ResCol>
                        </Row>
                      ) : null
                    }
                  </Form.Item>
                </>
              )}

              <Tabs />
              <Tabs onChange={handleChangeTabs}>
                <TabPane tab="Produtor" key="producer" forceRender>
                  <Row>
                    <SearchInput
                      showComponent={providerSettings.useFederalServiceCode}
                      label="Item da Lista de Serviço (LC 116/03)"
                      name={[
                        'producer',
                        'serviceInvoice',
                        'federalServiceCode'
                      ]}
                      placeholder="Selecione um item"
                      title="Selecione o item da lista de serviço"
                      resource="products/federal-services"
                    />
                    <SearchInput
                      showComponent={providerSettings.useNationalTaxationCode}
                      label="Código de Tributação Nacional"
                      name={[
                        'producer',
                        'serviceInvoice',
                        'nationalTaxationCode'
                      ]}
                      placeholder="Selecione um código"
                      title="Selecione um código de tributação nacional"
                      resource="products/national-taxation-code"
                    />
                    {providerSettings.useCityServiceCode && (
                      <ResCol span={12}>
                        <FormItemWrap
                          label={
                            providerSettings.provider === 'softplan'
                              ? 'CST'
                              : 'Código do serviço no munícipio'
                          }
                          name={['producer', 'serviceInvoice', 'cityServiceCode']}
                        >
                          <Input maxLength={20} />
                        </FormItemWrap>
                      </ResCol>
                    )}
                    <SearchInput
                      showComponent={
                        providerSettings.useNbsCode || issueType === 'annfs'
                      }
                      label="Código NBS"
                      name={['producer', 'serviceInvoice', 'nbsCode']}
                      placeholder="Selecione um código"
                      title="Selecione um código NBS"
                      resource="products/nbs-code"
                    />
                    <SearchInput
                      showComponent={providerSettings.useCnaeCode}
                      label="CNAE"
                      name={['producer', 'serviceInvoice', 'cnaeCode']}
                      placeholder="Selecione um CNAE"
                      title="Selecione o CNAE"
                      resource="cnaes"
                    />
                    {isSimplesNacional &&
                      providerSettings.useSimplesNacionalAnnex && (
                        <ResCol span={12}>
                          <FormItemWrap
                            label="Anexo Simples Nacional"
                            name={[
                              'producer',
                              'serviceInvoice',
                              'simplesNacionalAnnex'
                            ]}
                          >
                            <Input />
                          </FormItemWrap>
                        </ResCol>
                      )}
                    {!company.operationNatureEnabled && (
                      <ResCol span={12}>
                        <FormItemWrap
                          label="Tributação"
                          name={['producer', 'serviceInvoice', 'taxationGroup']}
                        >
                          <RemoteSelectCrud
                            resource="taxation-groups"
                            params={{ type: 'serviceInvoice' }}
                            initialValues={{ type: 'serviceInvoice' }}
                            textPropName="name"
                            resourceTitle="Tributação"
                            editComponent={TaxationGroupEdit}
                          />
                        </FormItemWrap>
                      </ResCol>
                    )}
                  </Row>
                </TabPane>
                <TabPane tab="Afiliado" key="affiliate" forceRender>
                  <Row>
                    <ResCol span={8}>
                      <FormItemWrap
                        label="Habilitar emissão de afiliado"
                        name={['affiliate', 'enabledAffiliateInvoices']}
                        valuePropName="checked"
                      >
                        <Switch onChange={handleChangeAffiliateEnabled} />
                      </FormItemWrap>
                    </ResCol>
                    <ResCol span={8}>
                      <FormItemWrap
                        label="Emitir notas automaticamente"
                        name={['affiliate', 'autoIssueInvoice']}
                        valuePropName="checked"
                      >
                        <Switch onChange={handleAutoIssueInvoice} />
                      </FormItemWrap>
                    </ResCol>
                    <ResCol span={8}>
                      <FormItemWrap
                        label="Competência"
                        name={['affiliate', 'effectiveMode']}
                      >
                        <Select disabled={!affiliateEnabled}>
                          <Select.Option value="inTheMonthOfOrder">
                            No mês das vendas
                          </Select.Option>
                          <Select.Option value="inTheMonthOfIssue">
                            No mês da emissão
                          </Select.Option>
                        </Select>
                      </FormItemWrap>
                    </ResCol>
                  </Row>
                  <FormItemWrap name={['affiliate', 'id']} hidden>
                    <Input />
                  </FormItemWrap>
                  <Row>
                    <SearchInput
                      showComponent={providerSettings.useFederalServiceCode}
                      label="Item da Lista de Serviço (LC 116/03)"
                      name={[
                        'affiliate',
                        'serviceInvoice',
                        'federalServiceCode'
                      ]}
                      placeholder="Selecione um item"
                      title="Selecione o item da lista de serviço"
                      resource="products/federal-services"
                      disabled={!affiliateEnabled}
                    />
                    <SearchInput
                      showComponent={providerSettings.useNationalTaxationCode}
                      label="Código de Tributação Nacional"
                      name={[
                        'affiliate',
                        'serviceInvoice',
                        'nationalTaxationCode'
                      ]}
                      placeholder="Selecione um código"
                      title="Selecione um código de tributação nacional"
                      resource="products/national-taxation-code"
                    />
                    {providerSettings.useCityServiceCode && (
                      <ResCol span={12}>
                        <FormItemWrap
                          label={
                            providerSettings.provider === 'softplan'
                              ? 'CST'
                              : 'Código do serviço no munícipio'
                          }
                          name={[
                            'affiliate',
                            'serviceInvoice',
                            'cityServiceCode'
                          ]}
                        >
                          <Input maxLength={20} disabled={!affiliateEnabled} />
                        </FormItemWrap>
                      </ResCol>
                    )}

                    <SearchInput
                      showComponent={
                        providerSettings.useNbsCode || issueType === 'annfs'
                      }
                      label="Código NBS"
                      name={['affiliate', 'serviceInvoice', 'nbsCode']}
                      placeholder="Selecione um código"
                      title="Selecione um código NBS"
                      resource="products/nbs-code"
                      disabled={!affiliateEnabled}
                    />
                    <SearchInput
                      showComponent={providerSettings.useCnaeCode}
                      label="CNAE"
                      name={['affiliate', 'serviceInvoice', 'cnaeCode']}
                      placeholder="Selecione um CNAE"
                      title="Selecione o CNAE"
                      resource="cnaes"
                      maxLength={15}
                      disabled={!affiliateEnabled}
                    />
                    {isSimplesNacional &&
                      providerSettings.useSimplesNacionalAnnex && (
                        <ResCol span={12}>
                          <FormItemWrap
                            label="Anexo Simples Nacional"
                            name={[
                              'affiliate',
                              'serviceInvoice',
                              'simplesNacionalAnnex'
                            ]}
                          >
                            <Input />
                          </FormItemWrap>
                        </ResCol>
                      )}
                    {!company.operationNatureEnabled && (
                      <ResCol span={12}>
                        <FormItemWrap
                          label="Tributação"
                          name={[
                            'affiliate',
                            'serviceInvoice',
                            'taxationGroup'
                          ]}
                        >
                          <RemoteSelectCrud
                            resource="taxation-groups"
                            params={{ type: 'serviceInvoice' }}
                            initialValues={{ type: 'serviceInvoice' }}
                            textPropName="name"
                            resourceTitle="Tributação"
                            editComponent={TaxationGroupEdit}
                          />
                        </FormItemWrap>
                      </ResCol>
                    )}
                  </Row>
                </TabPane>
              </Tabs>
              <Divider orientation="left">Tributação aproximada</Divider>
              <Row>
                <ResCol span={6}>
                  <FormItemWrap
                    name={[
                      'settings',
                      'serviceInvoice',
                      'approximateTaxes',
                      'mode'
                    ]}
                    label="Modo de exibição"
                  >
                    <Select>
                      <Select.Option value={APPROXIMATETAXES_DISABLED}>
                        Não exibir
                      </Select.Option>
                      <Select.Option value={APPROXIMATETAXES_DETAILED}>
                        {display[APPROXIMATETAXES_DETAILED]}
                      </Select.Option>
                      <Select.Option value={APPROXIMATETAXES_SIMPLIFIED}>
                        {display[APPROXIMATETAXES_SIMPLIFIED]}
                      </Select.Option>
                    </Select>
                  </FormItemWrap>
                </ResCol>
                <ResCol span={6}>
                  <FormItemWrap
                    name={[
                      'settings',
                      'serviceInvoice',
                      'approximateTaxes',
                      'useIbpt'
                    ]}
                    label="Utilizar IBPT"
                  >
                    <Select
                      disabled={
                        approximateTaxes.mode === APPROXIMATETAXES_DISABLED
                      }
                    >
                      <Select.Option value>Sim</Select.Option>
                      <Select.Option value={false}>Não</Select.Option>
                    </Select>
                  </FormItemWrap>
                </ResCol>
                {allowsSimplesNacionalRate && (
                  <ResCol span={12}>
                    <FormItemWrap
                      name={[
                        'settings',
                        'serviceInvoice',
                        'approximateTaxes',
                        'simplesNacionalRate'
                      ]}
                      label="Alíquota do Simples Nacional"
                    >
                      <InputPercent
                        disabled={
                          approximateTaxes.mode === APPROXIMATETAXES_DISABLED
                        }
                      />
                    </FormItemWrap>
                  </ResCol>
                )}
                <ResCol span={allowsSimplesNacionalRate ? 8 : 4}>
                  <FormItemWrap
                    name={[
                      'settings',
                      'serviceInvoice',
                      'approximateTaxes',
                      'national'
                    ]}
                    label="Federal"
                  >
                    <InputPercent
                      disabled={
                        approximateTaxes.mode === APPROXIMATETAXES_DISABLED ||
                        approximateTaxes.useIbpt
                      }
                    />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={allowsSimplesNacionalRate ? 8 : 4}>
                  <FormItemWrap
                    name={[
                      'settings',
                      'serviceInvoice',
                      'approximateTaxes',
                      'state'
                    ]}
                    label="Estadual"
                  >
                    <InputPercent
                      disabled={
                        approximateTaxes.mode === APPROXIMATETAXES_DISABLED ||
                        approximateTaxes.useIbpt
                      }
                    />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={allowsSimplesNacionalRate ? 8 : 4}>
                  <FormItemWrap
                    name={[
                      'settings',
                      'serviceInvoice',
                      'approximateTaxes',
                      'municipal'
                    ]}
                    label="Municipal"
                  >
                    <InputPercent
                      disabled={
                        approximateTaxes.mode === APPROXIMATETAXES_DISABLED ||
                        approximateTaxes.useIbpt
                      }
                    />
                  </FormItemWrap>
                </ResCol>
              </Row>
              <Form.Item
                name={[
                  'settings',
                  'serviceInvoice',
                  'approximateTaxes',
                  'imported'
                ]}
                hidden
              />
              <Form.Item
                name={[
                  'settings',
                  'serviceInvoice',
                  'approximateTaxes',
                  'expiresAt'
                ]}
                hidden
              />
              <Form.Item
                name={[
                  'settings',
                  'serviceInvoice',
                  'approximateTaxes',
                  'version'
                ]}
                hidden
              />
              {tab === 'producer' && (
                <>
                  {(isAdmin() || issueType === 'annfs') && (
                    <>
                      <Divider orientation="left">Outras opções</Divider>
                      <Row>
                        <ResCol span={11}>
                          <Form.Item
                            label="Enviar Inscrição Municipal"
                            name={[
                              'settings',
                              'serviceInvoice',
                              'sendCityTaxNumber'
                            ]}
                          >
                            <Select>
                              <Select.Option value="automatic">
                                Automático
                              </Select.Option>
                              <Select.Option value="yes">Sim</Select.Option>
                              <Select.Option value="no">Não</Select.Option>
                            </Select>
                          </Form.Item>
                        </ResCol>
                      </Row>
                    </>
                  )}
                  {isAdmin() && (
                    <>
                      <ResCol span={11}>
                        <Form.Item
                          label="Modo de dedução do Custo"
                          name={['settings', 'serviceInvoice', 'deductionMode']}
                        >
                          <Select>
                            <Select.Option value="none">Nenhum</Select.Option>
                            <Select.Option value="inInvoiceAmount">
                              No valor total da nota
                            </Select.Option>
                            <Select.Option value="inNetValue">
                              No valor líquido da nota
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      </ResCol>

                      <Row>
                        <ResCol span={18}>
                          <FormItemWrap
                            label="Cálculo do ISS"
                            name={[
                              'settings',
                              'serviceInvoice',
                              'issCalculationMode'
                            ]}
                          >
                            <Checkbox.Group
                              options={issCalculationModes}
                              value={checkedCalculationMode}
                              onChange={handleChangeCalculationMode}
                            />
                          </FormItemWrap>
                        </ResCol>
                      </Row>

                      <ResCol span={14}>
                        <FormItemWrap
                          name={[
                            'settings',
                            'serviceInvoice',
                            'receiverEmailDelivery'
                          ]}
                          label="Envio do e-mail do tomador na NFS-e"
                        >
                          <Select>
                            <Select.Option value="none">Não enviar para ninguém</Select.Option>
                            <Select.Option value="all">Enviar para todos</Select.Option>
                            <Select.Option value="naturalPersonOnly">Apenas Pessoas Físicas</Select.Option>
                            <Select.Option value="legalPersonOnly">Apenas Pessoas Jurídicas</Select.Option>
                          </Select>
                        </FormItemWrap>
                      </ResCol>
                      
                      <Row>
                      <ResCol span={11}>
                          <Form.Item
                            label="Utilizar intermediário de serviço na NFS-e"
                            name={[
                              'settings',
                              'serviceInvoice',
                              'useServiceIntermediary'
                            ]}
                          >
                            <Select>
                              <Select.Option value={true}>Sim</Select.Option>
                              <Select.Option value={false}>Não</Select.Option>
                            </Select>
                          </Form.Item>
                        </ResCol>
                    </Row>
                    </>
                  )}
                </>
              )}
            </TabPane>
          )}
          <TabPane tab="NF-e" key="nfe" forceRender>
            <Row>
              <ResCol span={8}>
                <FormItemWrap
                  name={['settings', 'productInvoice', 'environmentType']}
                  label="Ambiente"
                  tooltip={{
                    title: (
                      <div
                        style={{
                          maxWidth: 520,
                          lineHeight: 1.5
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <strong>Produção:</strong>
                          <div>Emissão com validade fiscal junto à SEFAZ.</div>
                        </div>

                        <div>
                          <strong>Homologação:</strong>
                          <div>
                            Testes no ambiente oficial da SEFAZ, sem validade
                            fiscal.
                          </div>
                        </div>
                      </div>
                    ),
                    overlayStyle: { maxWidth: 550 },
                    placement: 'right'
                  }}
                >
                  <EnvironmentTypeSelect invoiceModel={PRODUCTINVOICE} />
                </FormItemWrap>
              </ResCol>
              <ResCol span={8}>
                <FormItemWrap
                  name={['settings', 'productInvoice', 'series']}
                  label="Série"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={99999999999}
                  />
                </FormItemWrap>
              </ResCol>
              <ResCol span={8}>
                <FormItemWrap
                  name={['settings', 'productInvoice', 'nextNumber']}
                  label="Número da próxima NF-e"
                >
                  <InputNumber style={{ width: '100%' }} />
                </FormItemWrap>
              </ResCol>
            </Row>

            <Row>
              <ResCol span={8}>
                <FormItemWrap
                  name={['settings', 'productInvoice', 'danfePrintLayout']}
                  label="Layout do DANFE"
                >
                  <Select>
                    <Select.Option value="default">Padrão</Select.Option>
                    <Select.Option value="simplified">
                      Simplificado
                    </Select.Option>
                  </Select>
                </FormItemWrap>
              </ResCol>
              <ResCol span={24}>
                <FormItemWrap
                  name={['settings', 'useAutomaticDevolutionInvoice']}
                  label="Emitir nota fiscal de Devolução quando o prazo de cancelamento for ultrapassado?"
                  valuePropName="checked"
                  tooltip="Ao habilitar essa opção, será realizada a emissão de uma nota fiscal com a natureza de Devolução, quando houver cancelamento de uma venda com NF-e autorizada, após o prazo de cancelamento da nota fiscal no estado de origem."
                >
                  <Switch />
                </FormItemWrap>
              </ResCol>
            </Row>

            {hasInbound && (
              <>
                <Divider orientation="left">Notas recebidas</Divider>
                <Row gutter={16}>
                  <ResCol span={8}>
                    <FormItemWrap
                      name={['settings', 'productInvoice', 'inbound', 'enabled']}
                      label="Importar automaticamente"
                      tooltip="Busca automaticamente na distribuição da SEFAZ as NF-e emitidas contra o CNPJ da empresa."
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol span={8}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) =>
                        prev?.settings?.productInvoice?.inbound?.enabled !==
                        cur?.settings?.productInvoice?.inbound?.enabled
                      }
                    >
                      {({ getFieldValue }) => {
                        const enabled = getFieldValue([
                          'settings',
                          'productInvoice',
                          'inbound',
                          'enabled'
                        ]);

                        return (
                    <FormItemWrap
                      name={['settings', 'productInvoice', 'inbound', 'startDate']}
                      label="Importar a partir de"
                      tooltip={`Só notas emitidas a partir desta data são importadas (as anteriores são descartadas). No máximo ${inboundMaxRetroactiveDays} dias retroativos, que é também o histórico que a SEFAZ retém. Reduzir a data e salvar oferece reimportar o histórico a partir dela.`}
                      rules={[
                        {
                          required: enabled,
                          message: 'Informe a partir de quando importar.'
                        }
                      ]}
                      getValueProps={(value) => ({
                        value: value ? normalizeDate(value) : null
                      })}
                    >
                      <DatePicker
                        format="DD/MM/YYYY"
                        style={{ width: '100%' }}
                        disabledDate={isOutsideInboundWindow}
                      />
                    </FormItemWrap>
                        );
                      }}
                    </Form.Item>
                  </ResCol>
                </Row>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    prev?.settings?.productInvoice?.inbound?.enabled !==
                    cur?.settings?.productInvoice?.inbound?.enabled
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue([
                      'settings',
                      'productInvoice',
                      'inbound',
                      'enabled'
                    ]) ? (
                      <Row>
                        <ResCol span={16}>
                          <InboundInvoiceSyncStatus detailed />
                        </ResCol>
                      </Row>
                    ) : null
                  }
                </Form.Item>
              </>
            )}

            <Tabs>
              <TabPane tab="Produtor" key="producer" forceRender>
                <Row>
                  <ResCol span={12}>
                    <FormItemWrap
                      label="Código de Barras"
                      name={['producer', 'productInvoice', 'gtinCode']}
                    >
                      <Input maxLength={14} />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol span={12}>
                    <FormItemWrap
                      label="Unidade Comercial"
                      name={['producer', 'productInvoice', 'unitOfMeasurement']}
                      initialValue="UN"
                    >
                      <ProductUnitOfMeasurement />
                    </FormItemWrap>
                  </ResCol>
                </Row>

                <Row>
                  <ResCol span={12}>
                    <FormItemWrap
                      label="Unidade Tributável"
                      name={[
                        'producer',
                        'productInvoice',
                        'taxUnitOfMeasurement'
                      ]}
                      initialValue="UN"
                    >
                      <ProductUnitOfMeasurement />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol span={12}>
                    <FormItemWrap
                      label="Unidade Tributável (Exportação)"
                      name={[
                        'producer',
                        'productInvoice',
                        'exportationTaxUnitOfMeasurement'
                      ]}
                    >
                      <ProductUnitOfMeasurement />
                    </FormItemWrap>
                  </ResCol>
                </Row>

                {!company.operationNatureEnabled && (
                  <>
                    <Row>
                      <ResCol span={12}>
                        <FormItemWrap
                          label="CFOP Interno"
                          name={['producer', 'productInvoice', 'cfopIn']}
                        >
                          <CfopSelect />
                        </FormItemWrap>
                      </ResCol>
                      <ResCol span={12}>
                        <FormItemWrap
                          label="CFOP Internacional"
                          name={[
                            'producer',
                            'productInvoice',
                            'cfopInternational'
                          ]}
                        >
                          <CfopSelect />
                        </FormItemWrap>
                      </ResCol>
                    </Row>

                    <Row>
                      <ResCol span={12}>
                        <FormItemWrap
                          label="CFOP Interestadual (Não contribuinte)"
                          name={[
                            'producer',
                            'productInvoice',
                            'cfopInterstate'
                          ]}
                        >
                          <CfopSelect />
                        </FormItemWrap>
                      </ResCol>
                      <ResCol span={12}>
                        <FormItemWrap
                          label="CFOP Interestadual (Contribuinte)"
                          name={[
                            'producer',
                            'productInvoice',
                            'cfopTaxpayerInterstate'
                          ]}
                        >
                          <CfopSelect />
                        </FormItemWrap>
                      </ResCol>
                    </Row>
                  </>
                )}

                <Row>
                  <SearchInput
                    label="NCM"
                    name={['producer', 'productInvoice', 'ncm']}
                    title="NCM"
                    resource="ncms"
                  />

                  {!company.operationNatureEnabled && (
                    <ResCol span={12}>
                      <FormItemWrap
                        label="Tributação"
                        name={['producer', 'productInvoice', 'taxationGroup']}
                      >
                        <RemoteSelectCrud
                          resource="taxation-groups"
                          params={{ type: 'productInvoice' }}
                          initialValues={{ type: 'productInvoice' }}
                          textPropName="name"
                          resourceTitle="Tributação"
                          editComponent={TaxationGroupEdit}
                        />
                      </FormItemWrap>
                    </ResCol>
                  )}
                </Row>

                <Row>
                  <ResCol span={24}>
                    <FormItemWrap name={['producer', 'id']} hidden>
                      <Input />
                    </FormItemWrap>
                    <FormItemWrap
                      label="Informações adicionais"
                      name={[
                        'producer',
                        'productInvoice',
                        'additionalInformation'
                      ]}
                      whitespace
                    >
                      <Input.TextArea maxLength={2000} rows={3} />
                    </FormItemWrap>
                  </ResCol>
                </Row>
              </TabPane>
            </Tabs>
          </TabPane>

          <TabPane tab="Geral" key="settings" forceRender>
            <Divider className="gx-mt-0" orientation="left">
              Endereço
            </Divider>
            <Row>
              <ResCol>
                <FormItemWrap
                  name={['settings', 'addressResolutionMode']}
                  label="O que fazer ao emitir uma nota fiscal onde o cliente não está com endereço informado?"
                >
                  <Select
                    value={selectedOption}
                    onChange={handleSelectChange}
                    style={{ marginTop: '6px', maxWidth: '320px' }}
                  >
                    <Select.Option value="none">
                      Emitir sem o endereço do cliente
                    </Select.Option>
                    <Select.Option value="useCompanyAddress">
                      Emitir com o endereço da empresa
                    </Select.Option>
                    <Select.Option value="useDefaultAddress">
                      Emitir com um endereço padrão
                    </Select.Option>
                    <Select.Option value="useForeignAddress">
                      Emitir com endereço estrangeiro
                    </Select.Option>
                  </Select>
                </FormItemWrap>
              </ResCol>
              <ResCol>
                {selectedOption === 'useDefaultAddress' ? (
                  <AddressV2 fieldNames={['settings', 'defaultAddress']} />
                ) : null}
                {selectedOption === 'useForeignAddress' ? (
                  <AddressForForeign
                    required={false}
                    requiredCountry
                    fieldNames={['settings', 'defaultAddress']}
                  />
                ) : null}
                {selectedOption !== 'none' ? (
                  <>
                    <FormItemWrap
                      name={['settings', 'useDefaultAddressWhenIncomplete']}
                      valuePropName="checked"
                      label={
                        <>
                          Utilizar este endereço em clientes com endereço
                          incompleto
                          <TooltipInfoIcon
                            title={
                              <>
                                Este endereço será utilizado na emissão de notas
                                quando o endereço do cliente estiver incompleto.
                              </>
                            }
                            placement="rightTop"
                          />
                        </>
                      }
                    >
                      <Switch />
                    </FormItemWrap>
                    <Alert
                      message={
                        'Para utilização do endereço da empresa ou endereço padrão na nota fiscal, é primordial confirmar com sua contabilidade se este recurso não trará incidentes fiscais à sua empresa.'
                      }
                      showIcon
                      type="warning"
                    />
                  </>
                ) : null}
              </ResCol>
            </Row>

            <Divider className="gx-mt-0" orientation="left">
              Cópia da nota fiscal por e-mail
            </Divider>
            <Row>
              <ResCol>
                <FormItemWrap
                  label="Informe os e-mails para receber uma cópia de cada nota fiscal emitida"
                  name={['settings', 'invoiceCcEmails']}
                  validateTrigger="onBlur"
                  rules={[{ validator: emailValidator(5) }]}
                >
                  <Select mode="tags" style={{ width: '340px' }}>
                    {settings?.emails?.map((email) => {
                      return (
                        <Select.Option value={email}>{email}</Select.Option>
                      );
                    })}
                  </Select>
                </FormItemWrap>
              </ResCol>
            </Row>
            <Row>
              <ResCol>
                <FormItemWrap
                  name={['settings', 'includeInvoiceAttachments']}
                  label="Anexar no e-mail o PDF e XML da nota fiscal"
                  valuePropName="checked"
                >
                  <Switch />
                </FormItemWrap>
              </ResCol>
            </Row>

            <Divider orientation="left">Outras opções</Divider>
            <Row>
              <ResCol span={14}>
                <FormItemWrap
                  name={['settings', 'taxReformFieldsEnabled']}
                  label="Habilitar campos da Reforma Tributária?"
                  valuePropName="checked"
                >
                  <Switch />
                </FormItemWrap>
              </ResCol>
              {isAdmin() && (
                <>
                  <ResCol>
                    <FormItemWrap
                      name={['settings', 'allowMultipleInvoiceModelsPerOrder']}
                      label="Permitir produtos com modelos de nota fiscal diferentes para a mesma venda"
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol>
                    <FormItemWrap
                      name={['settings', 'useInvoiceNumeringPerProduct']}
                      label="Habilitar numeração de nota fiscal por produto"
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol>
                    <FormItemWrap
                      name={['settings', 'groupMonthlyClosingBySeries']}
                      label="Agrupar fechamento mensal por série da nota fiscal"
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>
                  <ResCol span={12}>
                    <FormItemWrap
                      name={['settings', 'invoiceIssuanceLimit']}
                      label="Limite de emissão de notas fiscais por ciclo"
                    >
                      <InputNumber
                        style={{ width: '45%' }}
                        min={0}
                        max={99999999999}
                      />
                    </FormItemWrap>
                  </ResCol>

                  <ResCol span={14}>
                    <FormItemWrap
                      name={['settings', 'productKitEnabled']}
                      label="Habilitar kit de produtos?"
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>

                  <ResCol span={12}>
                    <FormItemWrap
                      label="Modo de paginação"
                      name="paginationMode"
                      initialValue={paginationMode}
                    >
                      <Select
                        style={{ width: '45%' }}
                        onChange={handleChangePaginationMode}
                      >
                        <Select.Option value="automatic">
                          Automático
                        </Select.Option>
                        <Select.Option value="complete">Completo</Select.Option>
                        <Select.Option value="simplified">
                          Simplificado
                        </Select.Option>
                      </Select>
                    </FormItemWrap>
                  </ResCol>

                  <ResCol span={14}>
                    <FormItemWrap
                      name={['settings', 'decimalPrecision']}
                      label="Precisão decimal"
                      tooltip="Número de casas decimais aplicada nos itens da venda/nota fiscal."
                    >
                      <InputNumber style={{ width: '39%' }} min={0} max={100} />
                    </FormItemWrap>
                  </ResCol>

                  <ResCol span={14}>
                    <FormItemWrap
                      name={['settings', 'operationNatureEnabled']}
                      label="Habilitar Natureza de Operação?"
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItemWrap>
                  </ResCol>
                </>
              )}
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </DrawerEdit>
  );
};
export default ResourceEdit.create(CompanySettings, {
  resource: 'companies',
  currentAttr: 'editingSettings'
});
